import { useCallback, useEffect, useRef, useState } from 'react';
import { m, useReducedMotion } from 'motion/react';
import { Blanket } from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useSidebarActive } from '@/features/sidebar';
import { useOnboardingAnswersOptional } from '@/services/db/onboarding-answers/queries';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { MODERATE_02_ENTRANCE_PRODUCTIVE_TRANSITION, REDUCED_MOTION_TRANSITION } from '@/utils/motion';
import { useDidAgentAutoStart } from '../../agent-handoff';
import { OnboardingStepId, getStartingPointVariant, useOnboardingSteps } from '../../steps';
import { useVariantAssetPoll } from '../../use-variant-asset-poll';
import * as styles from './index.css';

type SpotlightLayerProps = {
  onRelease: () => void;
};

function SpotlightLayer({ onRelease }: SpotlightLayerProps) {
  const shouldReduceMotion = useReducedMotion();
  const steps = useOnboardingSteps();
  const { hasPermission } = usePermissions();
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  const answersQuery = useOnboardingAnswersOptional();

  const createStep = steps.find((step) => step.id === OnboardingStepId.CreateWorkflow);
  const hasCreatedAsset = createStep?.isComplete === true;

  // The agent builds the asset outside this tab, and nothing here invalidates
  // the list reads, so the scrim polls for its own release rather than
  // borrowing the checklist step's — that step unmounts the moment the agent
  // navigates away, which is most of the hand-off.
  useVariantAssetPoll({
    variant: getStartingPointVariant(answersQuery.data?.answers?.starting_point),
    isVariantPending: answersQuery.isLoading,
    workspaceId,
    hasPermission,
    isComplete: hasCreatedAsset,
  });

  useEffect(() => {
    if (hasCreatedAsset) {
      onRelease();
    }
  }, [hasCreatedAsset, onRelease]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onRelease();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onRelease]);

  if (hasCreatedAsset) {
    return null;
  }

  return (
    <m.div
      className={styles.frame}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition:
          shouldReduceMotion === true ? REDUCED_MOTION_TRANSITION : MODERATE_02_ENTRANCE_PRODUCTIVE_TRANSITION,
      }}
    >
      <Blanket withBackground isActive onClick={onRelease} />
    </m.div>
  );
}

export function AgentSetupSpotlight() {
  const didAutoStart = useDidAgentAutoStart();
  const isAgentOpen = useSidebarActive('copilot');
  const [isReleased, setIsReleased] = useState(false);
  const release = useCallback(() => setIsReleased(true), []);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!didAutoStart) {
      return;
    }
    if (isAgentOpen) {
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current) {
      setIsReleased(true);
    }
  }, [didAutoStart, isAgentOpen]);

  if (!didAutoStart || isReleased || !isAgentOpen) {
    return null;
  }

  return <SpotlightLayer onRelease={release} />;
}
